import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import SignatureCanvas from "../components/SignatureCanvas";
import {
  HardHat, LogOut, FileText, Briefcase, CheckCircle, XCircle, Clock,
  Upload, AlertCircle, DollarSign, Calendar, MapPin, Building2, Mail,
  Phone, Shield, FileCheck, Loader2, ChevronRight, Send, Eye, Download,
  MessageSquare, PenTool
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const SubcontractorPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subcontractor, setSubcontractor] = useState(null);
  const [offers, setOffers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [activeTab, setActiveTab] = useState("offers");
  const [company, setCompany] = useState(null);
  
  // Dialog states
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [acceptingOffer, setAcceptingOffer] = useState(false);
  
  // Accept form
  const [acceptForm, setAcceptForm] = useState({
    signature_data: null,
    notes: ""
  });
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    document_type_id: "",
    file: null,
    expiry_date: ""
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem("portal_token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = useCallback(async () => {
    try {
      const [meRes, offersRes, typesRes, companyRes] = await Promise.all([
        axios.get(`${API_BASE}/api/portal/me`, { headers: getAuthHeader() }),
        axios.get(`${API_BASE}/api/portal/offers`, { headers: getAuthHeader() }),
        axios.get(`${API_BASE}/api/portal/document-types`, { headers: getAuthHeader() }),
        axios.get(`${API_BASE}/api/portal/company`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
      ]);
      
      setSubcontractor(meRes.data);
      setOffers(offersRes.data);
      setDocuments(meRes.data.documents || []);
      setDocumentTypes(typesRes.data);
      setCompany(companyRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("portal_token");
    if (!token) {
      navigate("/subcontractor-portal/login");
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("portal_token");
    localStorage.removeItem("portal_sub");
    navigate("/subcontractor-portal/login");
  };

  const handleAcceptOffer = async () => {
    if (!acceptForm.signature_data) {
      toast.error("Please sign to accept the offer");
      return;
    }

    setAcceptingOffer(true);
    try {
      await axios.post(
        `${API_BASE}/api/portal/offers/${selectedOffer.offer_id}/accept`,
        acceptForm,
        { headers: getAuthHeader() }
      );
      toast.success("Offer accepted successfully!");
      setShowAcceptDialog(false);
      setShowOfferDialog(false);
      setAcceptForm({ signature_data: null, notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept offer");
    } finally {
      setAcceptingOffer(false);
    }
  };

  const handleDeclineOffer = async (offerId) => {
    if (!window.confirm("Are you sure you want to decline this offer?")) return;
    
    try {
      await axios.post(
        `${API_BASE}/api/portal/offers/${offerId}/decline`,
        { reason: "Declined by subcontractor" },
        { headers: getAuthHeader() }
      );
      toast.success("Offer declined");
      setShowOfferDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to decline offer");
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadForm.file || !uploadForm.document_type_id) {
      toast.error("Please select a file and document type");
      return;
    }

    setUploadingDoc(true);
    try {
      // First upload the file
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('file_type', 'document');
      
      const uploadRes = await axios.post(`${API_BASE}/api/upload-file`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      // Then create the document record
      await axios.post(
        `${API_BASE}/api/portal/documents`,
        {
          document_type_id: uploadForm.document_type_id,
          file_url: uploadRes.data.url,
          filename: uploadForm.file.name,
          expiry_date: uploadForm.expiry_date || null
        },
        { headers: getAuthHeader() }
      );

      toast.success("Document uploaded successfully!");
      setShowUploadDialog(false);
      setUploadForm({ document_type_id: "", file: null, expiry_date: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingDoc(false);
    }
  };

  const getOfferStatusBadge = (status) => {
    const styles = {
      sent: { color: "bg-blue-500/20 text-blue-400", icon: Send },
      pending: { color: "bg-amber-500/20 text-amber-400", icon: Clock },
      accepted: { color: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle },
      declined: { color: "bg-red-500/20 text-red-400", icon: XCircle },
      completed: { color: "bg-slate-500/20 text-slate-400", icon: CheckCircle }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge className={`${style.color} gap-1`}>
        <Icon size={12} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case 'complete': return 'text-emerald-400';
      case 'partial': return 'text-amber-400';
      default: return 'text-red-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-orange-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <HardHat className="h-6 w-6 text-orange-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{subcontractor?.company_name}</h1>
                <p className="text-sm text-slate-400">{subcontractor?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                subcontractor?.compliance_status === 'complete' 
                  ? 'bg-emerald-500/20' 
                  : 'bg-amber-500/20'
              }`}>
                <Shield size={16} className={getComplianceColor(subcontractor?.compliance_status)} />
                <span className={`text-sm font-medium ${getComplianceColor(subcontractor?.compliance_status)}`}>
                  {subcontractor?.compliance_status === 'complete' ? 'Compliant' : 'Documents Required'}
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} className="border-slate-600 text-slate-300">
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Active Offers</p>
                  <p className="text-2xl font-bold text-white">
                    {offers.filter(o => ['sent', 'pending'].includes(o.status)).length}
                  </p>
                </div>
                <Briefcase className="h-8 w-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Accepted</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    {offers.filter(o => o.status === 'accepted').length}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Documents</p>
                  <p className="text-2xl font-bold text-white">{documents.length}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Compliance</p>
                  <p className={`text-2xl font-bold ${getComplianceColor(subcontractor?.compliance_status)}`}>
                    {subcontractor?.compliance_status === 'complete' ? '100%' : 'Incomplete'}
                  </p>
                </div>
                <Shield className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'offers', label: 'Job Offers', icon: Briefcase },
            { id: 'documents', label: 'Documents', icon: FileText },
            { id: 'profile', label: 'Profile', icon: Building2 }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id 
                ? "bg-orange-500 hover:bg-orange-600" 
                : "border-slate-600 text-slate-300 hover:bg-slate-700"
              }
            >
              <tab.icon size={16} className="mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Job Offers Tab */}
        {activeTab === 'offers' && (
          <div className="space-y-4">
            {offers.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="py-12 text-center">
                  <Briefcase className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No job offers yet</p>
                  <p className="text-slate-500 text-sm mt-1">Check back later for new opportunities</p>
                </CardContent>
              </Card>
            ) : (
              offers.map(offer => (
                <Card 
                  key={offer.offer_id} 
                  className="bg-slate-800/50 border-slate-700 hover:border-orange-500/50 cursor-pointer transition-all"
                  onClick={() => { setSelectedOffer(offer); setShowOfferDialog(true); }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-white">{offer.description}</h3>
                          {getOfferStatusBadge(offer.status)}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          {offer.project_name && (
                            <div className="flex items-center gap-2 text-slate-400">
                              <Building2 size={14} />
                              <span>{offer.project_name}</span>
                            </div>
                          )}
                          {offer.trade && (
                            <div className="flex items-center gap-2 text-slate-400">
                              <HardHat size={14} />
                              <span>{offer.trade}</span>
                            </div>
                          )}
                          {offer.budget_amount && (
                            <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                              <DollarSign size={14} />
                              <span>${offer.budget_amount.toLocaleString()}</span>
                            </div>
                          )}
                          {offer.start_date && (
                            <div className="flex items-center gap-2 text-slate-400">
                              <Calendar size={14} />
                              <span>{new Date(offer.start_date).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-6 w-6 text-slate-500" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Compliance Documents</h2>
              <Button 
                onClick={() => setShowUploadDialog(true)}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Upload size={16} className="mr-2" />
                Upload Document
              </Button>
            </div>

            {/* Required Documents */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <CardTitle className="text-white">Required Documents</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {documentTypes.filter(dt => dt.required).map(docType => {
                  const uploaded = documents.find(d => d.document_type_id === docType.document_type_id);
                  return (
                    <div 
                      key={docType.document_type_id}
                      className="flex items-center justify-between p-4 border-b border-slate-700 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        {uploaded?.status === 'approved' ? (
                          <CheckCircle className="h-5 w-5 text-emerald-400" />
                        ) : uploaded ? (
                          <Clock className="h-5 w-5 text-amber-400" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-400" />
                        )}
                        <div>
                          <p className="text-white font-medium">{docType.name}</p>
                          <p className="text-slate-500 text-sm">{docType.description}</p>
                        </div>
                      </div>
                      {uploaded ? (
                        <Badge className={
                          uploaded.status === 'approved' 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-amber-500/20 text-amber-400'
                        }>
                          {uploaded.status === 'approved' ? 'Approved' : 'Pending Review'}
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setUploadForm({ ...uploadForm, document_type_id: docType.document_type_id });
                            setShowUploadDialog(true);
                          }}
                          className="border-orange-500 text-orange-400 hover:bg-orange-500/20"
                        >
                          Upload
                        </Button>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* All Documents */}
            {documents.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="border-b border-slate-700">
                  <CardTitle className="text-white">Uploaded Documents</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {documents.map(doc => (
                    <div 
                      key={doc.document_id}
                      className="flex items-center justify-between p-4 border-b border-slate-700 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <FileCheck className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="text-white font-medium">{doc.filename}</p>
                          <p className="text-slate-500 text-sm">
                            Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          doc.status === 'approved' 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : doc.status === 'rejected'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-amber-500/20 text-amber-400'
                        }>
                          {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                        </Badge>
                        <Button size="sm" variant="ghost" asChild>
                          <a href={`${API_BASE}${doc.file_url}`} target="_blank" rel="noopener noreferrer">
                            <Download size={16} />
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <CardTitle className="text-white">Company Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="text-slate-400 text-sm">Company Name</p>
                    <p className="text-white font-medium">{subcontractor?.company_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="text-slate-400 text-sm">Email</p>
                    <p className="text-white font-medium">{subcontractor?.email}</p>
                  </div>
                </div>
                {subcontractor?.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-slate-500" />
                    <div>
                      <p className="text-slate-400 text-sm">Phone</p>
                      <p className="text-white font-medium">{subcontractor?.phone}</p>
                    </div>
                  </div>
                )}
                {subcontractor?.address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-slate-500" />
                    <div>
                      <p className="text-slate-400 text-sm">Address</p>
                      <p className="text-white font-medium">{subcontractor?.address}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <CardTitle className="text-white">Trades & Licenses</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                {subcontractor?.trades?.length > 0 && (
                  <div>
                    <p className="text-slate-400 text-sm mb-2">Trades</p>
                    <div className="flex flex-wrap gap-2">
                      {subcontractor.trades.map(trade => (
                        <Badge key={trade} className="bg-slate-700 text-slate-300">
                          {trade}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {subcontractor?.license_number && (
                  <div>
                    <p className="text-slate-400 text-sm">License Number</p>
                    <p className="text-white font-medium">{subcontractor.license_number}</p>
                  </div>
                )}
                {subcontractor?.insurance_expiry && (
                  <div>
                    <p className="text-slate-400 text-sm">Insurance Expiry</p>
                    <p className="text-white font-medium">
                      {new Date(subcontractor.insurance_expiry).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Offer Detail Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Job Offer Details</DialogTitle>
          </DialogHeader>
          {selectedOffer && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">{selectedOffer.description}</h3>
                {getOfferStatusBadge(selectedOffer.status)}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {selectedOffer.project_name && (
                  <div>
                    <p className="text-slate-400 text-sm">Project</p>
                    <p className="text-white font-medium">{selectedOffer.project_name}</p>
                  </div>
                )}
                {selectedOffer.trade && (
                  <div>
                    <p className="text-slate-400 text-sm">Trade</p>
                    <p className="text-white font-medium">{selectedOffer.trade}</p>
                  </div>
                )}
                {selectedOffer.budget_amount && (
                  <div>
                    <p className="text-slate-400 text-sm">Budget</p>
                    <p className="text-emerald-400 font-bold text-xl">
                      ${selectedOffer.budget_amount.toLocaleString()}
                    </p>
                  </div>
                )}
                {selectedOffer.start_date && (
                  <div>
                    <p className="text-slate-400 text-sm">Start Date</p>
                    <p className="text-white font-medium">
                      {new Date(selectedOffer.start_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              {selectedOffer.scope_of_work && (
                <div>
                  <p className="text-slate-400 text-sm mb-2">Scope of Work</p>
                  <div className="bg-slate-900/50 rounded-lg p-4 text-slate-300 whitespace-pre-wrap">
                    {selectedOffer.scope_of_work}
                  </div>
                </div>
              )}

              {['sent', 'pending'].includes(selectedOffer.status) && (
                <div className="flex gap-3 pt-4 border-t border-slate-700">
                  <Button
                    variant="outline"
                    onClick={() => handleDeclineOffer(selectedOffer.offer_id)}
                    className="flex-1 border-red-500 text-red-400 hover:bg-red-500/20"
                  >
                    <XCircle size={16} className="mr-2" />
                    Decline
                  </Button>
                  <Button
                    onClick={() => setShowAcceptDialog(true)}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Accept Offer
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Accept Offer Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Accept Job Offer</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <p className="text-slate-300">
              By signing below, you agree to accept this job offer and commit to the work described.
            </p>

            <div>
              <label className="text-sm font-medium text-slate-300 block mb-3">Your Signature *</label>
              <SignatureCanvas
                onSignatureChange={(dataUrl) => setAcceptForm({ ...acceptForm, signature_data: dataUrl })}
                width={400}
                height={150}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-300 block mb-2">Notes (optional)</label>
              <Textarea
                value={acceptForm.notes}
                onChange={(e) => setAcceptForm({ ...acceptForm, notes: e.target.value })}
                placeholder="Any additional notes or questions..."
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAcceptDialog(false)}
                className="flex-1 border-slate-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAcceptOffer}
                disabled={acceptingOffer || !acceptForm.signature_data}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {acceptingOffer ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  <>
                    <PenTool size={16} className="mr-2" />
                    Sign & Accept
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300 block mb-2">Document Type *</label>
              <select
                value={uploadForm.document_type_id}
                onChange={(e) => setUploadForm({ ...uploadForm, document_type_id: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              >
                <option value="">Select type...</option>
                {documentTypes.map(dt => (
                  <option key={dt.document_type_id} value={dt.document_type_id}>
                    {dt.name} {dt.required ? '*' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-300 block mb-2">File *</label>
              <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center hover:border-orange-500/50 transition-colors">
                <input
                  type="file"
                  id="doc-upload"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                />
                <label htmlFor="doc-upload" className="cursor-pointer">
                  {uploadForm.file ? (
                    <div className="flex flex-col items-center">
                      <FileCheck className="h-10 w-10 text-orange-400 mb-2" />
                      <p className="text-white font-medium">{uploadForm.file.name}</p>
                      <p className="text-slate-400 text-sm mt-1">Click to change</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-10 w-10 text-slate-500 mb-2" />
                      <p className="text-white font-medium">Click to upload</p>
                      <p className="text-slate-400 text-sm mt-1">PDF, Word, or Images</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {documentTypes.find(dt => dt.document_type_id === uploadForm.document_type_id)?.has_expiry && (
              <div>
                <label className="text-sm font-medium text-slate-300 block mb-2">Expiry Date</label>
                <Input
                  type="date"
                  value={uploadForm.expiry_date}
                  onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowUploadDialog(false)}
                className="flex-1 border-slate-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUploadDocument}
                disabled={uploadingDoc || !uploadForm.file || !uploadForm.document_type_id}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                {uploadingDoc ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubcontractorPortal;
